import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Copy, Check, Share2, Eye, RefreshCw } from 'lucide-react';
import { Switch } from '../../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../ui/alert-dialog';

const urlIspettore = (token) => `${window.location.origin}/ispettore/${token}`;

const Azione = ({ icona: Icona, testo, onClick, testid, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className="h-10 px-3.5 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-800 flex items-center gap-2 hover:bg-slate-900 hover:text-white hover:border-slate-900 active:scale-95 transition-[background-color,color,border-color,transform] disabled:opacity-50 disabled:pointer-events-none">
    <Icona className="h-4 w-4" />{testo}
  </button>
);

const Rigenera = ({ onConferma, codice }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button type="button" data-testid={`owner-regenerate-btn-${codice}`} className="text-xs font-semibold text-rose-700 hover:underline flex items-center gap-1">
        <RefreshCw className="h-3.5 w-3.5" />Cambia link
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent data-testid="owner-regenerate-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Creare un nuovo link?</AlertDialogTitle>
        <AlertDialogDescription>Il QR attuale, anche se stampato o già inviato, smette subito di funzionare. Usalo se il link è finito nelle mani sbagliate.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="owner-regenerate-cancel">Annulla</AlertDialogCancel>
        <AlertDialogAction onClick={onConferma} data-testid="owner-regenerate-confirm" className="bg-rose-600 hover:bg-rose-700">Crea nuovo link</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ProfiloLink = ({ profilo, onAttivo, onRigenera, onAnteprima }) => {
  const [copiato, setCopiato] = useState(false);
  const url = urlIspettore(profilo.token);
  const c = profilo.codice;

  const copia = async () => {
    try { await navigator.clipboard.writeText(url); setCopiato(true); toast.success('Link copiato'); setTimeout(() => setCopiato(false), 2000); }
    catch { toast.error('Copia non riuscita: tieni premuto sul link per copiarlo'); }
  };
  const condividi = async () => {
    const titolo = `Cartella documenti – ${profilo.nome}`;
    if (navigator.share) { try { await navigator.share({ title: titolo, url }); } catch { /* condivisione annullata */ } return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${titolo}: ${url}`)}`, '_blank', 'noopener');
  };

  return (
    <div data-testid={`owner-profile-card-${c}`} className="bg-white rounded-2xl border border-slate-200 p-4 lg:p-5 grid gap-5 sm:grid-cols-[auto,1fr] items-start">
      <div className={`justify-self-center rounded-2xl p-3 bg-white border-2 transition-opacity ${profilo.attivo ? 'border-slate-900' : 'border-dashed border-slate-300 opacity-40'}`}>
        <QRCodeSVG value={url} size={176} level="M" data-testid={`owner-qr-canvas-${c}`} />
      </div>
      <div className="space-y-3 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="font-bold text-slate-900" data-testid="owner-profile-name">{profilo.nome}</h4>
            <p className="text-sm text-slate-600">{profilo.descrizione}</p>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 shrink-0 cursor-pointer">
            <Switch checked={profilo.attivo} onCheckedChange={onAttivo} data-testid={`owner-profile-toggle-${c}`} aria-label="Link attivo" />
            <span data-testid={`owner-profile-status-${c}`}>{profilo.attivo ? 'Attivo' : 'Sospeso'}</span>
          </label>
        </div>
        <p className="font-mono text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 truncate select-all" data-testid={`owner-link-url-${c}`}>{url}</p>
        {!profilo.attivo && <p className="text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2" data-testid={`owner-link-suspended-${c}`}>Link sospeso: chi lo apre vede “link non valido” finché non lo riattivi.</p>}
        <div className="flex flex-wrap gap-2">
          <Azione icona={copiato ? Check : Copy} testo="Copia link" onClick={copia} testid={`owner-copy-link-btn-${c}`} disabled={!profilo.attivo} />
          <Azione icona={Share2} testo="Condividi" onClick={condividi} testid={`owner-share-link-btn-${c}`} disabled={!profilo.attivo} />
          <Azione icona={Eye} testo="Vedi come l'ispettore" onClick={onAnteprima} testid={`owner-preview-inspector-btn-${c}`} />
        </div>
        <Rigenera onConferma={onRigenera} codice={c} />
      </div>
    </div>
  );
};
